import classNames from 'classnames';
import * as PropTypes from 'prop-types';
import React from 'react';
import _ from 'underscore';

import EditableComponent from 'notebook/mediocre/core-ui/EditableComponent';
import {Format} from 'notebook/mediocre/formatted-text/FormattersConstants';
import TimeEditableWrapper from 'notebook/protocols/editor/TimeEditableWrapper';
import TimerEditableWrapper from 'notebook/protocols/editor/TimerEditableWrapper';
import * as StepConstants from 'notebook/protocols/StepConstants';
import {Step} from 'notebook/protocols/StepConstants';
import Checkbox from 'notebook/protocols/viewer/Checkbox';
import {ElementKeyboardEvent} from 'util/Types';

interface StepsEditorEditableProps {
  cid: string;
  step: Step;
  index: number;
  updateTimeHandler: (time: number) => void;
  setSelection: (selection: SelectionRestoreData) => void;
  // Restore the contents of a protocol and the selection from edit history
  restoreState: (e: ElementKeyboardEvent, selectionRestoreData: {selection: SelectionRestoreData}) => boolean;
  // Store the contents of a protocol and the selection in edit history
  storeState: () => void;
  locked?: boolean;
  className?: string;
  text?: string;
  formats?: Format;
  spellCheck: boolean;
  inEntry: boolean;
  itemUpdateHandler: (content: object) => void;
}

const TimeEditable: React.FC<StepsEditorEditableProps> = (props: StepsEditorEditableProps): JSX.Element => (
  <TimeEditableWrapper
    step={props.step}
    index={props.index}
    isMultiEditable={false}
    updateTimeHandler={props.updateTimeHandler}
    setSelection={props.setSelection}
    storeState={props.storeState}
    restoreState={props.restoreState}
    showSeconds={false}
    locked={props.locked}
  />
);

class GenericStepEditable extends React.PureComponent<StepsEditorEditableProps> {
  override render(): JSX.Element {
    return (
      <div className="step-editable-wrapper">
        {this.props.step.type != null ? (
          <span
            className={classNames({
              'step-type': true,
              'step-type-critical': this.props.step.type === StepConstants.TYPE_CRITICAL,
              'step-type-pause': this.props.step.type === StepConstants.TYPE_PAUSE,
            })}
          >
            {this.props.step.type}
          </span>
        ) : undefined}
        <EditableComponent
          // eslint-disable-next-line no-restricted-syntax
          {..._.extend({}, this.props, {
            className: classNames(this.props.className, 'step-editable', 'multi-editable'),
            allowRichText: true,
            allowDefaultPaste: true,
            cid: this.props.cid,
            text: this.props.text,
            formats: this.props.formats,
            index: this.props.index,
            spellCheck: this.props.spellCheck,
          })}
        />
        {
          // We need a different div to hold the placeholder, since contenteditables do weird things
          // with an :after + text-indent and empty content. http://jsfiddle.net/pymok1wo/
          !this.props.locked ? <span className="step-placeholder">{null}</span> : undefined
        }
      </div>
    );
  }
}

export class HeaderEditable extends React.PureComponent<StepsEditorEditableProps> {
  override render(): JSX.Element {
    return (
      <ul className="headers">
        <li>
          <GenericStepEditable {...this.props} />
          <TimeEditable {...this.props} />
        </li>
      </ul>
    );
  }
}

export class StepEditable extends React.PureComponent<StepsEditorEditableProps> {
  static propTypes = {
    step: PropTypes.shape({stepCount: PropTypes.number.isRequired}).isRequired,
  };

  toggleComplete = (): void => {
    const {step} = this.props;
    this.props.itemUpdateHandler({text: step.text, formats: step.formats, completed: !step.completed});
  };

  override render(): JSX.Element {
    return (
      <ol className="steps">
        <li value={this.props.step.stepCount}>
          {this.props.inEntry ? (
            <Checkbox
              checked={this.props.step.completed}
              onClick={!this.props.locked ? this.toggleComplete : undefined}
            />
          ) : undefined}
          <GenericStepEditable {...this.props} />
          <TimeEditable {...this.props} />
        </li>
      </ol>
    );
  }
}

export class DescriptionEditable extends React.PureComponent<StepsEditorEditableProps> {
  override render(): JSX.Element {
    return (
      <ul className="descriptions">
        <li>
          <GenericStepEditable {...this.props} />
        </li>
      </ul>
    );
  }
}

interface StepTimerEditableProps {
  step: Step;
  index: number;
  updateTimerHandler: (time: number) => void;
  deleteHandler: () => void;
  setSelection: (selection: SelectionRestoreData) => void;
  // Restore the contents of a protocol and the selection from edit history
  restoreState: (e: ElementKeyboardEvent, selection: {selection: SelectionRestoreData}) => boolean;
  // Store the contents of a protocol and the selection in edit history
  storeState: () => void;
  locked?: boolean;
}

export class StepTimerEditable extends React.PureComponent<StepTimerEditableProps> {
  override render(): JSX.Element {
    return (
      <div className="timer-outer">
        <div>
          <TimerEditableWrapper
            step={this.props.step}
            index={this.props.index}
            locked={this.props.locked}
            isMultiEditable={true}
            updateTimeHandler={this.props.updateTimerHandler}
            setSelection={this.props.setSelection}
            deleteHandler={this.props.deleteHandler}
            storeState={this.props.storeState}
            restoreState={this.props.restoreState}
          />
        </div>
      </div>
    );
  }
}
